/**
 * חצרות — API Client לשרת
 */

// בפרודקשן ללא VITE_API_URL → קוראים לאותו דומין (relative). בפיתוח → localhost:3021.
const envUrl = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_API_URL;
const isDev = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.DEV;
const DEFAULT_BASE = isDev ? 'http://localhost:3021' : '';

let baseUrl: string = envUrl !== undefined ? envUrl : DEFAULT_BASE;
let token: string | null = localStorage.getItem('chatzerot_token');
let currentAccount: { id: string; email: string; displayName: string } | null = null;

const accStr = localStorage.getItem('chatzerot_account');
if (accStr) {
  try {
    currentAccount = JSON.parse(accStr);
  } catch {
    /* ignore */
  }
}

export function setApiBaseUrl(url: string): void {
  baseUrl = url;
}

export function getToken(): string | null {
  return token;
}

export function getAccount(): typeof currentAccount {
  return currentAccount;
}

export function isAuthenticated(): boolean {
  return token !== null;
}

export function logout(): void {
  token = null;
  currentAccount = null;
  localStorage.removeItem('chatzerot_token');
  localStorage.removeItem('chatzerot_account');
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers,
  });

  const body = (await res.json().catch(() => null)) as unknown;

  if (!res.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new ApiError(err?.message ?? `${res.status} ${res.statusText}`, res.status, err?.error);
  }

  return body as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// ═══════════════════════════════════════════════════════════════════
// Auth
// ═══════════════════════════════════════════════════════════════════

export async function register(input: {
  email: string;
  password: string;
  displayName: string;
}): Promise<{ token: string; account: NonNullable<typeof currentAccount> }> {
  const res = await apiFetch<{ token: string; account: NonNullable<typeof currentAccount> }>(
    '/auth/register',
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
  saveAuth(res.token, res.account);
  return res;
}

export async function login(input: {
  email: string;
  password: string;
}): Promise<{ token: string; account: NonNullable<typeof currentAccount> }> {
  const res = await apiFetch<{ token: string; account: NonNullable<typeof currentAccount> }>(
    '/auth/login',
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
  saveAuth(res.token, res.account);
  return res;
}

function saveAuth(t: string, acc: NonNullable<typeof currentAccount>): void {
  token = t;
  currentAccount = acc;
  localStorage.setItem('chatzerot_token', t);
  localStorage.setItem('chatzerot_account', JSON.stringify(acc));
}

// ═══════════════════════════════════════════════════════════════════
// Rounds
// ═══════════════════════════════════════════════════════════════════

export interface RoundSummary {
  id: string;
  name: string;
  tempo: 'blitz' | 'normal' | 'slow';
  status: 'waiting' | 'running' | 'finished' | 'cancelled';
  seed: string;
  engine_version: string;
  created_by: string;
  max_players: number;
  current_tick: number;
  total_ticks: number;
  seconds_per_tick: number;
  scheduled_start_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export interface RoundPlayer {
  id: string;
  round_id: string;
  account_id: string;
  dynasty_id: string;
  dynasty_name: string;
  ethnicity: string;
  stream: string;
  home_city_id: string;
  joined_at: string;
  is_active: boolean;
}

export async function listRounds(): Promise<RoundSummary[]> {
  const { rounds } = await apiFetch<{ rounds: RoundSummary[] }>('/rounds');
  return rounds;
}

export async function createRound(input: {
  name: string;
  tempo: 'blitz' | 'normal' | 'slow';
  maxPlayers?: number;
}): Promise<RoundSummary> {
  const { round } = await apiFetch<{ round: RoundSummary }>('/rounds', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return round;
}

export async function getRound(roundId: string): Promise<{
  round: RoundSummary;
  players: RoundPlayer[];
}> {
  return apiFetch(`/rounds/${roundId}`);
}

export async function joinRound(
  roundId: string,
  input: {
    dynastyName: string;
    ethnicity: string;
    stream: string;
    homeCityId: string;
  },
): Promise<{ player: RoundPlayer }> {
  return apiFetch(`/rounds/${roundId}/join`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function startRound(roundId: string): Promise<{ started: boolean }> {
  return apiFetch(`/rounds/${roundId}/start`, { method: 'POST' });
}

export async function getRoundState(roundId: string): Promise<{
  round: RoundSummary;
  player: RoundPlayer | null;
  state: unknown | null;
}> {
  return apiFetch(`/rounds/${roundId}/state`);
}

export async function submitIntent(
  roundId: string,
  intentType: string,
  payload: Record<string, unknown>,
): Promise<{ intentId: string }> {
  return apiFetch(`/rounds/${roundId}/intents`, {
    method: 'POST',
    body: JSON.stringify({ intentType, payload }),
  });
}
